import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Trash2,
  LogOut,
  ArrowDownToLine,
  Eye,
  EyeOff,
  Pencil,
  Search,
  X,
  Check,
  ShieldCheck,
  ShieldAlert,
  Clock,
  FileSpreadsheet,
} from "lucide-react";

function groupByDate(tickets) {
  const map = new Map();
  for (const t of tickets) {
    if (!map.has(t.date))
      map.set(t.date, { date: t.date, order_index: t.order_index, items: [] });
    map.get(t.date).items.push(t);
  }
  return Array.from(map.values()).sort((a, b) => a.order_index - b.order_index);
}

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all"); // all|active|inactive
  const [dateFilter, setDateFilter] = useState([]); // [] = all dates, otherwise array of dates
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null); // ticket object
  const [sessions, setSessions] = useState([]);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [hidePrices, setHidePrices] = useState(false);

  // Load global setting on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setHidePrices(!data.show_prices_public);
      } catch {
        // ignore — default visible
      }
    })();
  }, []);

  const toggleHidePrices = async () => {
    const next = !hidePrices;
    setHidePrices(next); // optimistic
    try {
      await api.patch("/admin/settings", { show_prices_public: !next });
      toast.success(next ? "Prix masqués pour les clients." : "Prix visibles pour les clients.");
    } catch (e) {
      setHidePrices(!next); // revert
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/admin/tickets");
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/admin/sessions");
      const next = data.sessions || [];
      // Skip state update if nothing materially changed → prevents flicker
      setSessions((prev) => {
        if (prev.length !== next.length) return next;
        const key = (s) => `${s.id}|${s.status}|${s.user_name || ""}|${s.label || ""}`;
        const same = prev.every((s, i) => key(s) === key(next[i]));
        return same ? prev : next;
      });
    } catch (e) {
      // 403 means non-owner — silently ignore
      if (e?.response?.status !== 403) {
        toast.error(formatApiError(e?.response?.data?.detail) || e.message);
      }
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  // Sessions: poll only when modal is open (avoids flicker + saves bandwidth)
  useEffect(() => {
    if (user?.role !== "owner") return;
    loadSessions(); // initial fetch for badge count
    if (!sessionsOpen) return;
    const t = setInterval(loadSessions, 10000);
    return () => clearInterval(t);
  }, [user?.role, sessionsOpen]);

  const onImport = async () => {
    if (!raw.trim()) {
      toast.error("Collez au moins une ligne.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tickets/import", { raw });
      toast.success(`${data.inserted} billet(s) ajouté(s).`);
      setRaw("");
      await loadTickets();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onToggle = async (id) => {
    try {
      const { data } = await api.patch(`/admin/tickets/${id}/toggle`);
      setTickets((prev) =>
        prev.map((t) => (t.id === id ? { ...t, active: data.active } : t))
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/admin/tickets/${id}`);
      setTickets((prev) => prev.filter((t) => t.id !== id));
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
      toast.success("Billet supprimé.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onClearAll = async () => {
    try {
      await api.delete("/admin/tickets");
      setTickets([]);
      setSelected(new Set());
      toast.success("Stock vidé.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onLogout = () => {
    logout();
    nav("/admin/login");
  };

  const onBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    try {
      await api.post("/admin/tickets/bulk", { ids, action });
      if (action === "delete") {
        setTickets((prev) => prev.filter((t) => !selected.has(t.id)));
      } else {
        const flag = action === "activate";
        setTickets((prev) =>
          prev.map((t) => (selected.has(t.id) ? { ...t, active: flag } : t))
        );
      }
      setSelected(new Set());
      toast.success(
        action === "delete"
          ? `${ids.length} billet(s) supprimé(s).`
          : action === "activate"
            ? `${ids.length} billet(s) diffusé(s).`
            : `${ids.length} billet(s) retiré(s).`
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onSaveEdit = async (form) => {
    try {
      const { data } = await api.patch(`/admin/tickets/${editing.id}`, form);
      setTickets((prev) => prev.map((t) => (t.id === editing.id ? data : t)));
      toast.success("Billet modifié.");
      setEditing(null);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onSavePrix = async (id, prixStr) => {
    const trimmed = String(prixStr).replace(",", ".").trim();
    const prix = trimmed === "" ? null : parseFloat(trimmed);
    if (prix !== null && (Number.isNaN(prix) || prix < 0)) {
      toast.error("Prix invalide");
      return;
    }
    try {
      const { data } = await api.patch(`/admin/tickets/${id}`, { prix });
      setTickets((prev) => prev.map((t) => (t.id === id ? data : t)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onApproveSession = async (id) => {
    try {
      const { data } = await api.patch(`/admin/sessions/${id}/approve`);
      setSessions((prev) => prev.map((s) => (s.id === id ? data : s)));
      toast.success("Accès accordé.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onRevokeSession = async (id) => {
    try {
      const { data } = await api.patch(`/admin/sessions/${id}/revoke`);
      setSessions((prev) => prev.map((s) => (s.id === id ? data : s)));
      toast.success("Accès révoqué.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onDeleteSession = async (id) => {
    try {
      await api.delete(`/admin/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      toast.success("Session supprimée.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onRenameSession = async (id, label) => {
    try {
      const { data } = await api.patch(`/admin/sessions/${id}/label`, { label });
      setSessions((prev) => prev.map((s) => (s.id === id ? data : s)));
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  const onRenameUser = async (userId, name) => {
    try {
      await api.patch(`/admin/users/${userId}/name`, { name });
      // Apply name to all sessions of that user in local state
      setSessions((prev) =>
        prev.map((s) => (s.user_id === userId ? { ...s, user_name: name } : s)),
      );
      toast.success(name ? `Prénom mis à jour : ${name}` : "Prénom retiré.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  };

  // Derivations
  const allDates = useMemo(() => {
    const seen = new Map();
    for (const t of tickets) {
      if (!seen.has(t.date)) seen.set(t.date, t.order_index);
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1] - b[1])
      .map(([d]) => d);
  }, [tickets]);

  const visible = useMemo(() => {
    let arr = tickets;
    if (filter === "active") arr = arr.filter((t) => t.active);
    if (filter === "inactive") arr = arr.filter((t) => !t.active);
    if (dateFilter.length > 0) arr = arr.filter((t) => dateFilter.includes(t.date));
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      arr = arr.filter(
        (t) =>
          t.categorie.toLowerCase().includes(q) ||
          t.date.toLowerCase().includes(q) ||
          (t.block || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [tickets, filter, dateFilter, search]);

  const groups = useMemo(() => groupByDate(visible), [visible]);

  const totalActive = tickets.filter((t) => t.active).length;
  const totalAll = tickets.length;

  // Selection helpers
  const visibleIds = useMemo(() => visible.map((t) => t.id), [visible]);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => n.delete(id));
      } else {
        visibleIds.forEach((id) => n.add(id));
      }
      return n;
    });
  };

  const toggleSelectGroup = (items) => {
    const ids = items.map((t) => t.id);
    const allSel = ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const n = new Set(prev);
      if (allSel) ids.forEach((id) => n.delete(id));
      else ids.forEach((id) => n.add(id));
      return n;
    });
  };

  const toggleSelectOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const clearFilters = () => {
    setFilter("all");
    setDateFilter([]);
    setSearch("");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-neutral-200 sticky top-0 bg-white z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-6 min-w-0">
            <div className="min-w-0">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Console admin
              </div>
              <div className="font-serif-display text-xl sm:text-2xl truncate">
                Céline Dion · Stock
              </div>
            </div>
            {user?.role === "owner" && (
              <button
                onClick={() => setSessionsOpen(true)}
                className="relative flex items-center gap-2 border-2 border-black px-3 py-2 text-[11px] sm:text-xs uppercase tracking-widest font-semibold hover:bg-black hover:text-white transition-colors"
                data-testid="admin-security-button"
              >
                <ShieldCheck className="w-4 h-4" />
                <span className="hidden xs:inline sm:inline">Sécurité</span>
                {(() => {
                  const pending = sessions.filter((s) => s.status === "pending").length;
                  return pending > 0 ? (
                    <span
                      className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full min-w-[22px] h-[22px] px-1 flex items-center justify-center text-[11px] font-bold border-2 border-white shadow"
                      data-testid="admin-security-badge"
                    >
                      {pending}
                    </span>
                  ) : null;
                })()}
              </button>
            )}
            <button
              onClick={() => nav("/admin/drive")}
              className="flex items-center gap-2 border-2 border-black px-3 py-2 text-[11px] sm:text-xs uppercase tracking-widest font-semibold hover:bg-black hover:text-white transition-colors"
              data-testid="admin-drive-button"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden sm:inline">Drive</span>
            </button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            <span
              className="hidden md:inline-block font-mono-data text-[11px] text-neutral-500 max-w-[160px] truncate"
              data-testid="admin-user-email"
            >
              {user?.email}
            </span>
            <Button
              onClick={onLogout}
              variant="outline"
              className="rounded-none border-black hover:bg-neutral-50 px-3"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        {/* Stats */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-0 border border-neutral-200">
          <div className="p-6 border-r border-neutral-200">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Lignes
            </div>
            <div
              className="font-serif-display text-4xl mt-1"
              data-testid="admin-stat-lines"
            >
              {tickets.length}
            </div>
          </div>
          <div className="p-6 border-r border-neutral-200">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Total places
            </div>
            <div
              className="font-serif-display text-4xl mt-1"
              data-testid="admin-stat-total"
            >
              {totalAll}
            </div>
          </div>
          <div className="p-6 border-r border-neutral-200">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Diffusés
            </div>
            <div
              className="font-serif-display text-4xl mt-1 text-black"
              data-testid="admin-stat-active"
            >
              {totalActive}
            </div>
          </div>
          <div className="p-6">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">
              Retirés
            </div>
            <div
              className="font-serif-display text-4xl mt-1 text-neutral-400"
              data-testid="admin-stat-inactive"
            >
              {totalAll - totalActive}
            </div>
          </div>
        </section>

        {/* Import area */}
        <section className="border border-neutral-200">
          <div className="p-6 border-b border-neutral-200">
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Étape 01
            </div>
            <h2 className="font-serif-display text-3xl mt-1">
              Coller le stock
            </h2>
            <p className="text-sm text-neutral-500 mt-1">
              Une ligne par billet. Format :{" "}
              <span className="font-mono-data text-[11px]">
                DATE • CATEGORIE • QTY • BLOCK • RANG • SIEGE
              </span>{" "}
              (séparé par tabulations). Colonnes dans le désordre ? Collez <strong>avec la ligne
              d'en-têtes</strong> : elles seront détectées automatiquement.
            </p>
          </div>
          <div className="p-6">
            <Textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder="sam 12 sept  TRIBUNE OR      2       I       100     45"
              rows={10}
              className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
              data-testid="admin-import-textarea"
            />
            <div className="flex items-center justify-between mt-4">
              <span className="font-mono-data text-[11px] text-neutral-500">
                {raw.split("\n").filter((l) => l.trim()).length} ligne(s) prêtes
              </span>
              <Button
                onClick={onImport}
                disabled={busy}
                className="rounded-none bg-black hover:bg-neutral-800 text-white"
                data-testid="admin-import-button"
              >
                <ArrowDownToLine className="w-4 h-4 mr-2" />
                {busy ? "Import…" : "Ajouter au stock"}
              </Button>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="space-y-4 sticky top-[72px] sm:top-[80px] z-20 bg-white -mx-6 px-6 py-4 border-b border-neutral-200 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.08)]" data-testid="admin-filters-sticky">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-0 border border-neutral-300">
              {[
                { v: "all", l: "Tous" },
                { v: "active", l: "Diffusés" },
                { v: "inactive", l: "Retirés" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setFilter(opt.v)}
                  className={`px-4 py-2 text-xs uppercase tracking-widest border-r border-neutral-300 last:border-r-0 ${
                    filter === opt.v
                      ? "bg-black text-white"
                      : "bg-white text-neutral-700 hover:bg-neutral-50"
                  }`}
                  data-testid={`admin-filter-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="w-[260px] flex items-center justify-between gap-2 px-3 py-2 rounded-none border border-neutral-300 hover:border-black focus:outline-none focus:border-black text-sm bg-white"
                  data-testid="admin-date-filter"
                >
                  <span className="truncate capitalize text-left">
                    {dateFilter.length === 0
                      ? <span className="text-neutral-500">Toutes les dates</span>
                      : dateFilter.length === 1
                        ? dateFilter[0]
                        : `${dateFilter.length} dates sélectionnées`}
                  </span>
                  <ChevronDown className="w-4 h-4 text-neutral-500 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="rounded-none p-0 w-[260px] max-h-[380px] overflow-hidden flex flex-col"
              >
                <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-200 bg-neutral-50">
                  <button
                    type="button"
                    onClick={() => setDateFilter(allDates.slice())}
                    className="text-[10px] uppercase tracking-widest text-neutral-700 hover:text-black"
                    data-testid="admin-date-filter-select-all"
                  >
                    Tout
                  </button>
                  <span className="text-[10px] font-mono-data text-neutral-500">
                    {dateFilter.length}/{allDates.length}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDateFilter([])}
                    className="text-[10px] uppercase tracking-widest text-neutral-700 hover:text-black"
                    data-testid="admin-date-filter-clear"
                  >
                    Aucun
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {allDates.map((d) => {
                    const checked = dateFilter.includes(d);
                    return (
                      <label
                        key={d}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm capitalize select-none ${
                          checked ? "bg-black text-white" : "hover:bg-neutral-100"
                        }`}
                        data-testid={`admin-date-filter-option-${d}`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setDateFilter((prev) =>
                              v ? [...prev, d] : prev.filter((x) => x !== d)
                            );
                          }}
                          className={`rounded-none ${checked ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-black" : "border-neutral-400"}`}
                        />
                        <span className="truncate">{d}</span>
                      </label>
                    );
                  })}
                  {allDates.length === 0 && (
                    <div className="px-3 py-6 text-center text-xs text-neutral-500">
                      Aucune date disponible
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Recherche catégorie / block / date…"
                className="pl-9 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="admin-search-input"
              />
            </div>

            {(filter !== "all" || dateFilter.length > 0 || search) && (
              <button
                onClick={clearFilters}
                className="text-xs uppercase tracking-widest text-neutral-500 hover:text-black flex items-center gap-1"
                data-testid="admin-clear-filters"
              >
                <X className="w-3.5 h-3.5" /> Effacer
              </button>
            )}

            <div className="flex-1" />

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className={`rounded-none border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 ${user?.role === "owner" ? "" : "hidden"}`}
                  data-testid="admin-clear-all-button"
                  disabled={tickets.length === 0}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Tout vider
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif-display text-2xl">
                    Êtes-vous sûr ?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Vous êtes sur le point de supprimer <strong>{tickets.length} ligne(s)</strong> du stock.
                    Cette action est <strong>irréversible</strong> — il faudra recoller tout le stock depuis Google Drive.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none" data-testid="admin-clear-all-cancel">
                    Annuler
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onClearAll}
                    className="rounded-none bg-red-600 hover:bg-red-700"
                    data-testid="admin-clear-all-confirm"
                  >
                    Oui, tout supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div
              className="flex items-center gap-3 border border-black bg-black text-white px-4 py-3"
              data-testid="admin-bulk-bar"
            >
              <span className="font-mono-data text-[11px] uppercase tracking-[0.2em]">
                {selected.size} sélectionné(s)
              </span>
              <div className="flex-1" />
              <Button
                onClick={() => onBulk("activate")}
                className="rounded-none bg-white text-black hover:bg-neutral-200 h-9"
                data-testid="admin-bulk-activate"
              >
                <Eye className="w-4 h-4 mr-2" /> Diffuser
              </Button>
              <Button
                onClick={() => onBulk("deactivate")}
                className="rounded-none bg-white text-black hover:bg-neutral-200 h-9"
                data-testid="admin-bulk-deactivate"
              >
                <EyeOff className="w-4 h-4 mr-2" /> Retirer
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="rounded-none bg-red-600 hover:bg-red-700 text-white h-9"
                    data-testid="admin-bulk-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-serif-display text-2xl">
                      Supprimer {selected.size} ligne(s) ?
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      Action irréversible.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-none">
                      Annuler
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => onBulk("delete")}
                      className="rounded-none bg-red-600 hover:bg-red-700"
                      data-testid="admin-bulk-delete-confirm"
                    >
                      Confirmer
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs uppercase tracking-widest text-white/70 hover:text-white ml-2"
                data-testid="admin-bulk-clear"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {visible.length > 0 && (
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Checkbox
                checked={allVisibleSelected}
                onCheckedChange={toggleSelectAll}
                className="rounded-none border-neutral-400"
                data-testid="admin-select-all"
              />
              <span className="font-mono-data uppercase tracking-widest">
                Tout sélectionner ({visible.length})
              </span>
            </div>
          )}
        </section>

        {/* Tickets table */}
        <section className="space-y-10" data-testid="admin-tickets-list">
          {groups.length === 0 && (
            <div
              className="border border-neutral-200 p-12 text-center"
              data-testid="admin-empty"
            >
              <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] text-neutral-400">
                Aucun billet
              </div>
              <p className="mt-3 font-serif-display text-3xl">
                {tickets.length === 0
                  ? "Collez votre stock ci-dessus pour démarrer."
                  : "Aucun résultat avec ces filtres."}
              </p>
            </div>
          )}
          {groups.map((g) => {
            const groupAllSel = g.items.every((t) => selected.has(t.id));
            return (
              <div key={g.date}>
                <div className="flex items-center justify-between border-b border-black pb-2 mb-3 gap-3">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={groupAllSel}
                      onCheckedChange={() => toggleSelectGroup(g.items)}
                      className="rounded-none border-neutral-400"
                      data-testid={`admin-select-group-${g.date.replace(/\s+/g, "-")}`}
                    />
                    <h3 className="font-serif-display text-2xl capitalize">
                      {g.date}
                    </h3>
                  </div>
                  <span className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                    {g.items.length} ligne(s)
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-500 border-b border-neutral-200">
                        <th className="py-2 pr-3 font-normal w-8"></th>
                        <th className="py-2 pr-3 font-normal">Catégorie</th>
                        <th className="py-2 pr-3 font-normal">Block</th>
                        <th className="py-2 pr-3 font-normal">Rang</th>
                        <th className="py-2 pr-3 font-normal">Siège</th>
                        <th className="py-2 pr-3 font-normal text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <span>Prix (€)</span>
                            <button
                              onClick={toggleHidePrices}
                              className="text-neutral-400 hover:text-black transition-colors"
                              title={hidePrices ? "Afficher les prix aux clients" : "Masquer les prix aux clients"}
                              data-testid="admin-toggle-prices"
                              aria-label={hidePrices ? "Afficher les prix aux clients" : "Masquer les prix aux clients"}
                            >
                              {hidePrices ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </th>
                        <th className="py-2 pr-3 font-normal text-center">
                          Diffusé
                        </th>
                        <th className="py-2 font-normal text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.items.map((t) => (
                        <tr
                          key={t.id}
                          className={`border-b border-neutral-100 ${
                            t.active
                              ? "bg-white"
                              : "bg-neutral-50 text-neutral-400"
                          } ${selected.has(t.id) ? "ring-1 ring-black" : ""}`}
                          data-testid={`admin-row-${t.id}`}
                        >
                          <td className="py-2 pr-3">
                            <Checkbox
                              checked={selected.has(t.id)}
                              onCheckedChange={() => toggleSelectOne(t.id)}
                              className="rounded-none border-neutral-400"
                              data-testid={`admin-select-${t.id}`}
                            />
                          </td>
                          <td className="py-2 pr-3">{t.categorie}</td>
                          <td className="py-2 pr-3 font-mono-data">
                            {t.block || "—"}
                          </td>
                          <td className="py-2 pr-3 font-mono-data">
                            {t.rang || "—"}
                          </td>
                          <td className="py-2 pr-3 font-mono-data">
                            {t.siege || "—"}
                          </td>
                          <td className="py-2 pr-3">
                            {hidePrices ? (
                              <div
                                className="font-mono-data text-sm text-neutral-300 tracking-widest text-right select-none"
                                data-testid={`admin-prix-hidden-${t.id}`}
                              >
                                ••• €
                              </div>
                            ) : (
                              <PriceCell
                                ticket={t}
                                onSave={(v) => onSavePrix(t.id, v)}
                              />
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center justify-center gap-2">
                              <Switch
                                checked={t.active}
                                onCheckedChange={() => onToggle(t.id)}
                                data-testid={`admin-toggle-${t.id}`}
                              />
                              {t.active ? (
                                <Eye className="w-3.5 h-3.5 text-black" />
                              ) : (
                                <EyeOff className="w-3.5 h-3.5 text-neutral-400" />
                              )}
                            </div>
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => setEditing(t)}
                                className="text-neutral-400 hover:text-black transition-colors"
                                data-testid={`admin-edit-${t.id}`}
                                aria-label="Modifier"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => onDelete(t.id)}
                                className="text-neutral-400 hover:text-red-600 transition-colors"
                                data-testid={`admin-delete-${t.id}`}
                                aria-label="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </section>

        {/* Sessions section moved to dialog (top-bar button "Sécurité") */}
      </main>

      {/* Sessions dialog */}
      <Dialog open={sessionsOpen} onOpenChange={setSessionsOpen}>
        <DialogContent className="rounded-none max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="admin-sessions-dialog">
          <DialogHeader>
            <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500">
              Sécurité
            </div>
            <DialogTitle className="font-serif-display text-3xl">
              Accès & sessions
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              Toute personne qui se connecte avec votre email + mot de passe arrive
              en « En attente ». Approuvez uniquement les vôtres.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4" data-testid="admin-sessions-list">
            {sessions.length === 0 && (
              <div className="text-sm text-neutral-500">Aucune session.</div>
            )}
            {sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                onApprove={() => onApproveSession(s.id)}
                onRevoke={() => onRevokeSession(s.id)}
                onDelete={() => onDeleteSession(s.id)}
                onRename={(label) => onRenameSession(s.id, label)}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <EditDialog
        ticket={editing}
        onClose={() => setEditing(null)}
        onSave={onSaveEdit}
      />
    </div>
  );
}

function EditDialog({ ticket, onClose, onSave }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (ticket) {
      setForm({
        categorie: ticket.categorie,
        prix: ticket.prix == null ? "" : String(ticket.prix),
        block: ticket.block || "",
        rang: ticket.rang || "",
        siege: ticket.siege || "",
      });
    } else {
      setForm(null);
    }
  }, [ticket]);

  if (!ticket || !form) return null;

  const set = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.categorie.trim()) return;
    const trimmed = form.prix.replace(",", ".").trim();
    const prix = trimmed === "" ? null : parseFloat(trimmed);
    if (prix !== null && (Number.isNaN(prix) || prix < 0)) return;
    onSave({
      categorie: form.categorie,
      block: form.block,
      rang: form.rang,
      siege: form.siege,
      prix,
    });
  };

  return (
    <Dialog open={!!ticket} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="rounded-none max-w-lg"
        data-testid="admin-edit-dialog"
      >
        <DialogHeader>
          <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Édition · {ticket.date}
          </div>
          <DialogTitle className="font-serif-display text-3xl">
            Modifier le billet
          </DialogTitle>
          <DialogDescription className="sr-only">
            Modifiez les champs du billet et enregistrez.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-neutral-700">
              Catégorie
            </Label>
            <Input
              value={form.categorie}
              onChange={set("categorie")}
              className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
              data-testid="admin-edit-categorie"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-700">
                Prix (€)
              </Label>
              <Input
                type="text"
                inputMode="decimal"
                value={form.prix}
                onChange={set("prix")}
                placeholder="ex: 120"
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="admin-edit-prix"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-700">
                Block
              </Label>
              <Input
                value={form.block}
                onChange={set("block")}
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="admin-edit-block"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-700">
                Rang
              </Label>
              <Input
                value={form.rang}
                onChange={set("rang")}
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="admin-edit-rang"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-neutral-700">
                Siège
              </Label>
              <Input
                value={form.siege}
                onChange={set("siege")}
                className="rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-black"
                data-testid="admin-edit-siege"
              />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-none"
              data-testid="admin-edit-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="rounded-none bg-black hover:bg-neutral-800 text-white"
              data-testid="admin-edit-save"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PriceCell({ ticket, onSave }) {
  const initial = ticket.prix == null ? "" : String(ticket.prix);
  const [value, setValue] = useState(initial);

  useEffect(() => {
    setValue(ticket.prix == null ? "" : String(ticket.prix));
  }, [ticket.prix]);

  const commit = () => {
    const before = ticket.prix == null ? "" : String(ticket.prix);
    if (value === before) return;
    onSave(value);
  };

  return (
    <div className="flex items-center justify-end gap-1">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          }
          if (e.key === "Escape") {
            setValue(ticket.prix == null ? "" : String(ticket.prix));
            e.currentTarget.blur();
          }
        }}
        placeholder="—"
        className="w-20 text-right font-mono-data text-sm bg-transparent border border-transparent hover:border-neutral-300 focus:border-black focus:outline-none px-2 py-1"
        data-testid={`admin-prix-${ticket.id}`}
      />
      <span className="text-xs text-neutral-400">€</span>
    </div>
  );
}


function SessionRow({ session, onApprove, onRevoke, onDelete, onRename, onRenameUser }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(session.label || "");
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(session.user_name || "");

  useEffect(() => {
    setLabel(session.label || "");
  }, [session.label]);

  useEffect(() => {
    setName(session.user_name || "");
  }, [session.user_name]);

  const status = session.status;
  const statusBadge =
    status === "approved" ? (
      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold border border-green-300">
        <ShieldCheck className="w-3 h-3" /> Approuvé
      </span>
    ) : status === "pending" ? (
      <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold border border-amber-300">
        <Clock className="w-3 h-3" /> En attente
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 bg-neutral-100 text-neutral-600 px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold border border-neutral-300">
        <ShieldAlert className="w-3 h-3" /> Révoqué
      </span>
    );

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className={`border ${
        status === "pending" ? "border-amber-400 bg-amber-50/40" : "border-neutral-200"
      } p-4 flex flex-col sm:flex-row sm:items-center gap-4`}
      data-testid={`admin-session-${session.id}`}
    >
      <div className="flex-1 min-w-0 space-y-1">
        {/* Prénom (per-user, editable inline) — only for non-owner sub-admins */}
        {session.user_role !== "owner" && (
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono-data text-[10px] uppercase tracking-[0.25em] text-neutral-400">
              Prénom
            </span>
            {editingName ? (
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  setEditingName(false);
                  const trimmed = name.trim();
                  if (trimmed !== (session.user_name || "")) onRenameUser(trimmed);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") {
                    setName(session.user_name || "");
                    setEditingName(false);
                  }
                }}
                placeholder="Ajouter un prénom"
                className="font-serif-display text-base border-b border-black bg-transparent focus:outline-none px-1 min-w-[120px]"
                data-testid={`admin-session-name-input-${session.id}`}
              />
            ) : (
              <button
                onClick={() => setEditingName(true)}
                className={`font-serif-display text-base hover:underline ${session.user_name ? "text-black" : "text-neutral-400 italic"}`}
                data-testid={`admin-session-name-${session.id}`}
              >
                {session.user_name || "+ ajouter"}
              </button>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {editing ? (
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onBlur={() => {
                setEditing(false);
                if (label.trim() && label !== session.label) onRename(label);
                else setLabel(session.label || "");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
                if (e.key === "Escape") {
                  setLabel(session.label || "");
                  setEditing(false);
                }
              }}
              className="font-serif-display text-lg border-b border-black bg-transparent focus:outline-none"
            />
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="font-serif-display text-lg hover:underline"
              data-testid={`admin-session-rename-${session.id}`}
            >
              {session.label || "Session"}
            </button>
          )}
          {session.user_role === "owner" ? (
            <span className="inline-flex items-center gap-1 bg-black text-white px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold">
              Propriétaire
            </span>
          ) : null}
          {statusBadge}
        </div>
        {session.user_email ? (
          <div className="font-mono-data text-xs text-neutral-600">
            {session.user_email}
          </div>
        ) : null}
        <div className="font-mono-data text-[11px] text-neutral-500 flex flex-wrap gap-x-4 gap-y-1">
          <span>IP : {session.ip || "—"}</span>
          <span>Créée : {fmt(session.created_at)}</span>
          {session.approved_at && <span>Approuvée : {fmt(session.approved_at)}</span>}
          {session.last_seen && <span>Vu : {fmt(session.last_seen)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {status === "pending" && (
          <Button
            onClick={onApprove}
            className="rounded-none bg-black text-white hover:bg-neutral-800 h-9"
            data-testid={`admin-session-approve-${session.id}`}
          >
            <Check className="w-4 h-4 mr-1" /> Approuver
          </Button>
        )}
        {status === "approved" && (
          <Button
            onClick={onRevoke}
            variant="outline"
            className="rounded-none border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700 h-9"
            data-testid={`admin-session-revoke-${session.id}`}
          >
            <ShieldAlert className="w-4 h-4 mr-1" /> Révoquer
          </Button>
        )}
        <button
          onClick={onDelete}
          className="text-neutral-400 hover:text-red-600 transition-colors p-2"
          data-testid={`admin-session-delete-${session.id}`}
          aria-label="Supprimer"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
